// ScoreGrid.js — a compact scoreline grid: square cells that scale with the
// page width, each filled square coloured by its count (the heatmap's bands)
// and linking to that scoreline's page. The club pages use it with goals
// scored across and conceded up; the season pages with home goals across and
// away goals up, ringing the season's first-ever results in gold.

import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box } from "@mui/material";
import HeatmapLegend from "./heatmap/HeatmapLegend";
import { CELL_TEXT, EMPTY_CELL, bandFor, legendBands, rgb } from "./heatmap/utils";
import { scorelineKey } from "./clubs/data";

const GOLD = "#ffc83c";

// counts: Map "x-y" -> count. labelFor(x, y, count) names a square for
// screen readers and the tooltip. gold: Set of "x-y" keys to ring.
export default function ScoreGrid({ counts, maxGoals, xLabel, yLabel, labelFor, gold }) {
  const n = maxGoals + 1;
  const bands = legendBands(Math.max(1, ...counts.values()));
  const cells = [];
  for (let y = n - 1; y >= 0; y--) {
    cells.push(
      <div key={`y${y}`} className="ax y">
        {y}
      </div>
    );
    for (let x = 0; x < n; x++) {
      const key = `${x}-${y}`;
      const count = counts.get(key);
      if (!count) {
        cells.push(<div key={key} className="cell empty" />);
        continue;
      }
      const label = labelFor(x, y, count);
      cells.push(
        <RouterLink
          key={key}
          to={`/scorelines/${scorelineKey(x, y)}`}
          className={`cell${gold?.has(key) ? " gold" : ""}`}
          title={label}
          aria-label={label}
          style={{ background: rgb(bandFor(count, bands).color) }}
        >
          {count}
        </RouterLink>
      );
    }
  }
  cells.push(<div key="corner" />);
  for (let x = 0; x < n; x++)
    cells.push(
      <div key={`x${x}`} className="ax x">
        {x}
      </div>
    );

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "stretch", maxWidth: 620 }}>
        <Box sx={{ writingMode: "vertical-rl", transform: "rotate(180deg)", textAlign: "center", color: "#e6e8ee", fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", pr: 0.5 }}>
          {yLabel}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `22px repeat(${n}, minmax(0, 1fr))`,
              gap: { xs: "2px", sm: "3px" },
              "& .cell": {
                aspectRatio: "1 / 1",
                borderRadius: { xs: "4px", sm: "6px" },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: CELL_TEXT,
                fontWeight: 800,
                fontSize: { xs: n > 9 ? 9.5 : 11, sm: n > 9 ? 12 : 14 },
                fontFeatureSettings: '"tnum"',
                textDecoration: "none",
                boxShadow: "inset 0 -2px 0 rgba(0,0,0,0.18)",
                transition: "transform 120ms ease-out, filter 120ms ease-out",
                "&:hover, &:focus-visible": { transform: "scale(1.08)", filter: "brightness(1.12)", outline: "none", zIndex: 1 },
              },
              "& .cell.gold": { outline: `2px solid ${GOLD}`, outlineOffset: "1px", zIndex: 1 },
              "& .cell.empty": { background: EMPTY_CELL, border: "1px solid rgba(255,255,255,0.05)", boxShadow: "none" },
              "& .ax": { color: "#d7dae3", fontSize: { xs: 10.5, sm: 12.5 }, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" },
              "& .ax.y": { justifyContent: "flex-end", pr: 0.5 },
            }}
          >
            {cells}
          </Box>
          <Box sx={{ textAlign: "center", color: "#e6e8ee", fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", mt: 1, pl: "22px" }}>
            {xLabel}
          </Box>
        </Box>
      </Box>
      <Box sx={{ maxWidth: 620, mt: 2 }}>
        <HeatmapLegend bands={bands} horizontal />
      </Box>
    </Box>
  );
}
